// Competitors panel — reusable for Account and Deal detail pages.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { BulkModal } from "@/components/bulk-modal";
import { Plus, Trash2 } from "lucide-react";

interface Props {
  /** "/accounts/:id/competitors" or "/deals/:id/competitors" */
  endpoint: string;
  /** "Account" or "Deal" — controls whether outcome is editable. */
  scope: "Account" | "Deal";
}

export function CompetitorsPanel({ endpoint, scope }: Props) {
  const qc = useQueryClient();
  const queryKey = [endpoint];
  const [addOpen, setAddOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(endpoint)).data.data as any[],
  });

  const rows = data ?? [];

  async function remove(linkId: string) {
    if (!confirm("Remove this competitor link?")) return;
    await api.delete(`${endpoint}/${linkId}`);
    qc.invalidateQueries({ queryKey });
  }

  async function updateLink(linkId: string, patch: Record<string, unknown>) {
    await api.put(`${endpoint}/${linkId}`, patch);
    qc.invalidateQueries({ queryKey });
  }

  const threatVariant = (t: string) =>
    t === "CRITICAL" || t === "HIGH" ? "destructive" :
    t === "MEDIUM" ? "default" : "secondary";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{rows.length} competitor{rows.length === 1 ? "" : "s"} linked</div>
        <Button size="sm" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-4 w-4"/>Add competitor</Button>
      </div>

      {isLoading && <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">No competitors yet.</p>
      )}

      {rows.length > 0 && (
        <ul className="divide-y rounded-md border">
          {rows.map((l) => {
            const c = l.competitor;
            return (
              <li key={l.id} className="group p-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link to={`/competitors/${c.id}`} className="font-medium hover:underline">{c.name}</Link>
                    <Badge variant={threatVariant(c.threatLevel) as any}>{c.threatLevel}</Badge>
                    {c.website && (
                      <a href={c.website} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:underline">
                        {new URL(c.website).hostname.replace(/^www\./, "")}
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {scope === "Deal" && (
                      <Select
                        value={l.outcome ?? ""}
                        onChange={(e) => updateLink(l.id, { outcome: e.target.value || null })}
                        className="h-7 w-32 text-xs"
                      >
                        <option value="">— Outcome —</option>
                        <option value="won">Won (we won)</option>
                        <option value="lost">Lost (they won)</option>
                        <option value="tied">Tied</option>
                        <option value="withdrew">They withdrew</option>
                      </Select>
                    )}
                    <button
                      onClick={() => remove(l.id)}
                      className="opacity-0 group-hover:opacity-100 text-destructive"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-4 w-4"/>
                    </button>
                  </div>
                </div>
                {l.notes && <p className="mt-1 text-xs text-muted-foreground">{l.notes}</p>}
              </li>
            );
          })}
        </ul>
      )}

      <AddCompetitorModal open={addOpen} onClose={() => setAddOpen(false)} endpoint={endpoint} scope={scope} />
    </div>
  );
}

function AddCompetitorModal({
  open, onClose, endpoint, scope,
}: { open: boolean; onClose: () => void; endpoint: string; scope: "Account" | "Deal" }) {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [competitorId, setCompetitorId] = useState("");
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ["picker-competitors", q],
    queryFn: async () => (await api.get("/competitors", { params: { q, pageSize: 25, isActive: true } })).data.data as any[],
    enabled: open,
  });

  const rows = useMemo(() => data ?? [], [data]);

  const mut = useMutation({
    mutationFn: async () => {
      const payload: any = { competitorId, notes: notes || undefined };
      if (scope === "Deal" && outcome) payload.outcome = outcome;
      await api.post(endpoint, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [endpoint] });
      setCompetitorId(""); setNotes(""); setOutcome(""); setQ(""); setError(null); onClose();
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Failed to add."),
  });

  return (
    <BulkModal
      open={open}
      onClose={() => { setCompetitorId(""); setError(null); onClose(); }}
      title="Add competitor"
      description={`Link a competitor to this ${scope.toLowerCase()}.`}
      confirmLabel="Add"
      confirmDisabled={!competitorId}
      busy={mut.isPending}
      onConfirm={() => mut.mutate()}
      error={error}
    >
      <div>
        <Label>Search competitors</Label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name…" />
      </div>
      <div className="max-h-48 overflow-auto rounded-md border">
        {isFetching && <p className="p-3 text-sm text-muted-foreground">Loading…</p>}
        {!isFetching && rows.length === 0 && <p className="p-3 text-sm text-muted-foreground">No competitors. <a href="/competitors/new" className="text-primary hover:underline">Create one →</a></p>}
        {rows.map((c) => (
          <label key={c.id} className="flex cursor-pointer items-center gap-2 border-b px-3 py-1.5 text-sm last:border-0 hover:bg-muted">
            <input type="radio" name="competitor" checked={competitorId === c.id} onChange={() => setCompetitorId(c.id)} />
            <span className="font-medium">{c.name}</span>
            <span className="ml-auto text-xs text-muted-foreground">{c.threatLevel}</span>
          </label>
        ))}
      </div>
      {scope === "Deal" && (
        <div>
          <Label htmlFor="outcome">Outcome (optional)</Label>
          <Select id="outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="">—</option>
            <option value="won">Won (we won)</option>
            <option value="lost">Lost (they won)</option>
            <option value="tied">Tied</option>
            <option value="withdrew">They withdrew</option>
          </Select>
        </div>
      )}
      <div>
        <Label htmlFor="notes">Notes</Label>
        <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Why they're in this deal/account…" />
      </div>
    </BulkModal>
  );
}
